import React, { useEffect, useState } from 'react';
import { supabase } from '../../services/supabaseClient';
import { Box, Typography, CircularProgress, Grid, Paper, IconButton } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Import icons
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CancelIcon from '@mui/icons-material/Cancel';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ArrowBack from '@mui/icons-material/ArrowBack';

const Dashboard = () => {
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch total orders count
        const { count: totalOrders } = await supabase
          .from('orders')
          .select('*', { count: 'exact', head: true });

        // Fetch pending orders count
        const { count: pendingOrders } = await supabase
          .from('orders')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending');

        // Fetch confirmed orders count
        const { count: confirmedOrders } = await supabase
          .from('orders')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'confirmed');

        // Fetch delivered orders count
        const { count: deliveredOrders } = await supabase
          .from('orders')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'delivered');

        // Fetch cancelled orders count
        const { count: cancelledOrders } = await supabase
          .from('orders')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'cancelled');

        // Fetch shipped orders count
        const { count: shippedOrders } = await supabase
          .from('orders')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'shipped');

        // Fetch total revenue from delivered orders
        const { data: revenueData } = await supabase
          .from('orders')
          .select('total_amount')
          .eq('status', 'delivered');
        const totalRevenue =
          revenueData?.reduce((sum, order) => sum + Number(order.total_amount), 0) || 0;

        // Fetch total available products count
        const { count: totalProducts } = await supabase
          .from('products')
          .select('*', { count: 'exact', head: true });

        setStats({
          totalOrders,
          pendingOrders,
          confirmedOrders,
          deliveredOrders,
          cancelledOrders,
          shippedOrders,
          totalRevenue,
          totalProducts
        });
      } catch (error) {
        console.error('Dashboard error:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isAdmin) fetchStats();
  }, [isAdmin]);

  if (!isAdmin) return <Typography variant="h5">غير مصرح بالوصول</Typography>;
  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );

  // Data for each stat card
  const statCards = [
    {
      title: 'إجمالي الطلبات',
      value: stats.totalOrders,
      icon: <ShoppingCartIcon fontSize="large" sx={{ color: 'white' }} />,
      bgColor: 'linear-gradient(45deg, #2196F3, #21CBF3)'
    },
    {
      title: 'الطلبات المعلقة',
      value: stats.pendingOrders,
      icon: <HourglassEmptyIcon fontSize="large" sx={{ color: 'white' }} />,
      bgColor: 'linear-gradient(45deg, #FFC107, #FFECB3)'
    },
    {
      title: 'الطلبات المؤكدة',
      value: stats.confirmedOrders,
      icon: <DoneAllIcon fontSize="large" sx={{ color: 'white' }} />,
      bgColor: 'linear-gradient(45deg, #4CAF50, #81C784)'
    },
    {
      title: 'الطلبات المسلمة',
      value: stats.deliveredOrders,
      icon: <CheckCircleOutlineIcon fontSize="large" sx={{ color: 'white' }} />,
      bgColor: 'linear-gradient(45deg, #00C853, #B9F6CA)'
    },
    {
      title: 'الطلبات الملغاة',
      value: stats.cancelledOrders,
      icon: <CancelIcon fontSize="large" sx={{ color: 'white' }} />,
      bgColor: 'linear-gradient(45deg, #F44336, #E57373)'
    },
    {
      title: 'الطلبات المشحونة',
      value: stats.shippedOrders,
      icon: <LocalShippingIcon fontSize="large" sx={{ color: 'white' }} />,
      bgColor: 'linear-gradient(45deg, #FF9800, #FFB74D)'
    },
    {
      title: 'إجمالي المبيعات',
      value: `${stats.totalRevenue.toFixed(2)} جنيه`,
      icon: <MonetizationOnIcon fontSize="large" sx={{ color: 'white' }} />,
      bgColor: 'linear-gradient(45deg, #9C27B0, #BA68C8)'
    },
    {
      title: 'المنتجات المتاحة',
      value: stats.totalProducts,
      icon: <StorefrontIcon fontSize="large" sx={{ color: 'white' }} />,
      bgColor: 'linear-gradient(45deg, #3F51B5, #7986CB)'
    }
  ];

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        background: 'linear-gradient(135deg, #f5f7fa, #c3cfe2)',
        minHeight: '100vh'
      }}
    >
      <IconButton onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        <ArrowBack />
      </IconButton>
      
      <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4, fontWeight: 'bold' }}>
        لوحة التحكم
      </Typography>

      <Grid container spacing={3}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: 4,
                textAlign: 'center',
                background: card.bgColor,
                color: 'white',
                transition: 'transform 0.3s',
                '&:hover': { transform: 'scale(1.03)' }
              }}
            >
              <Box sx={{ mb: 1 }}>{card.icon}</Box>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                {card.title}
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {card.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Dashboard;
